import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Button, message, Table, Tag, Modal, Form, Input, Select,
  DatePicker, Space, Statistic, Tabs, InputNumber, Popconfirm, Switch,
} from 'antd';
import {
  DollarOutlined, DeleteOutlined, ReloadOutlined, PayCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiFetch, notifyDataChanged } from '../utils/api.js';

const LEVEL_COLOR = { '普通': 'default', '重点': 'blue', 'VIP': 'gold' };
const PAY_METHODS = ['转账', '现金', '微信', '支付宝', '其他'];

// 千分位金额
const fmtMoney = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '0.00';
  const neg = n < 0;
  const parts = Math.abs(n).toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + parts.join('.');
};
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

// ===== 登记回款弹窗（选客户 → 选订单冲抵） =====
function PaymentModal({ open, customers, initCustomerId, onCancel, onDone }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [custId, setCustId] = useState(null);
  const [detail, setDetail] = useState(null); // { customer, orders }
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedNos, setSelectedNos] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const payAmount = Number(Form.useWatch('amount', form)) || 0;

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ date: dayjs(), method: '转账', order_nos: [] });
      setSelectedNos([]);
      setDetail(null);
      setCustId(initCustomerId || null);
      if (initCustomerId) loadDetail(initCustomerId);
      loadAccounts();
    }
  }, [open, initCustomerId]);

  const loadAccounts = async () => {
    try {
      const res = await apiFetch('/api/cash-account').then(r => r.json());
      const list = (res && res.accounts) || [];
      setAccounts(list);
      const def = list.find(a => String(a.name || '').includes('淮安乐兜')) || list[0];
      if (def) form.setFieldValue('cash_account_id', def.id);
    } catch (e) { /* 忽略 */ }
  };

  const loadDetail = async (id) => {
    if (!id) { setDetail(null); return; }
    setLoadingDetail(true);
    try {
      const res = await apiFetch(`/api/crm/customers/${id}`).then(r => r.json());
      setDetail(res && res.customer ? res : null);
    } catch (e) { setDetail(null); }
    setLoadingDetail(false);
  };

  const changeCustomer = (id) => {
    setCustId(id);
    setSelectedNos([]);
    form.setFieldValue('order_nos', []);
    loadDetail(id);
  };

  // 未收清订单，按时间倒序
  const unsettled = ((detail && detail.orders) || [])
    .map(o => ({ ...o, __rec: round2((Number(o.order_price) || 0) - (Number(o.paid_amount) || 0)) }))
    .filter(o => o.__rec > 0.001)
    .sort((a, b) => String(b.order_date || b.shipping_date || '').localeCompare(String(a.order_date || a.shipping_date || '')));

  // 分摊预览（按应收比例，最后一单吸收误差）
  const pickedOrders = unsettled.filter(o => selectedNos.includes(o.platform_order_no));
  const totalRec = round2(pickedOrders.reduce((s, o) => s + o.__rec, 0));
  const pay = Math.min(payAmount, totalRec);
  const allocations = pickedOrders.map((o, i) => {
    let inc;
    if (i === pickedOrders.length - 1) {
      inc = Math.max(0, Math.min(o.__rec, round2(pay - pickedOrders.slice(0, i).reduce((s, x) => s + round2(pay * (x.__rec / (totalRec || 1))), 0))));
    } else {
      inc = round2(pay * (o.__rec / (totalRec || 1)));
    }
    return { no: o.platform_order_no, rec: o.__rec, inc };
  });

  const submit = async () => {
    try {
      const v = await form.validateFields();
      if (!custId) { message.error('请选择客户'); return; }
      setSaving(true);
      const res = await apiFetch('/api/crm/payments', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: custId,
          amount: v.amount,
          date: v.date.format('YYYY-MM-DD'),
          method: v.method,
          order_nos: Array.isArray(v.order_nos) ? v.order_nos : [],
          remark: v.remark,
          cash_account_id: v.cash_account_id,
        }),
      }).then(r => r.json());
      setSaving(false);
      if (res.success) { message.success('回款已登记'); onDone(); } else message.error(res.error || '登记失败');
    } catch (e) { setSaving(false); if (e && e.errorFields) return; message.error('登记失败'); }
  };

  const cust = customers.find(c => c.id === custId);

  return (
    <Modal title="登记回款" open={open} onCancel={onCancel} onOk={submit} okText="登记" confirmLoading={saving} width={720} destroyOnClose>
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label="客户" required>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="选择客户"
                value={custId}
                onChange={changeCustomer}
                loading={loadingDetail}
                options={customers.map(c => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}><Form.Item name="amount" label="回款金额（元）" rules={[{ required: true, message: '必填' }]}><InputNumber min={0.01} precision={2} style={{ width: '100%' }} placeholder="本次打款金额" /></Form.Item></Col>
          <Col span={8}>
            <Form.Item name="date" label="回款日期" rules={[{ required: true, message: '必填' }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
          </Col>
          <Col span={8}><Form.Item name="method" label="回款方式"><Select options={PAY_METHODS.map(m => ({ value: m, label: m }))} /></Form.Item></Col>
          <Col span={8}>
            <Form.Item name="cash_account_id" label="资金账户" rules={[{ required: true, message: '请选择入账账户' }]}
              extra={<span style={{ fontSize: 11, color: '#bfbfbf' }}>回款入账后计入该账户余额</span>}>
              <Select
                placeholder="选择入账账户"
                options={accounts.map(a => ({ value: a.id, label: `${a.name}（余额 ¥${fmtMoney(a.balance)}）` }))}
              />
            </Form.Item>
          </Col>
          <Col span={16}><Form.Item name="remark" label="备注"><Input placeholder="选填" /></Form.Item></Col>
          <Col span={24}>
            <Form.Item name="order_nos" label="关联订单（多选冲抵应收，未选则不冲订单）" extra={pickedOrders.length === 0 ? '不选则仅记客户回款，不冲抵订单应收' : `已选 ${pickedOrders.length} 单，应收合计 ¥${fmtMoney(totalRec)}`}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="value"
                placeholder={!custId ? '请先选客户' : (loadingDetail ? '加载订单中…' : '可多选未收清订单，留空则不冲抵')}
                disabled={!custId || loadingDetail}
                popupMatchSelectWidth={false}
                listHeight={300}
                maxTagCount="responsive"
                value={selectedNos}
                onChange={(v) => { setSelectedNos(v || []); form.setFieldValue('order_nos', v || []); }}
                options={unsettled.map(o => ({
                  value: o.platform_order_no,
                  label: o.platform_order_no,
                  __total: Number(o.order_price) || 0,
                  __paid: Number(o.paid_amount) || 0,
                  __rec: o.__rec,
                }))}
                optionRender={(opt) => (
                  <div style={{ padding: '4px 2px' }}>
                    <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, fontWeight: 600, color: '#26201C' }}>{opt.data.value}</div>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                      成交 ¥{fmtMoney(opt.data.__total)} · 已收 ¥{fmtMoney(opt.data.__paid)} · 应收 <span style={{ color: '#D85A30', fontWeight: 600 }}>¥{fmtMoney(opt.data.__rec)}</span>
                    </div>
                  </div>
                )}
                tagRender={(props) => (
                  <Tag style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 }} closable={props.closable} onClose={props.onClose}>{props.label}</Tag>
                )}
              />
            </Form.Item>
          </Col>
          {allocations.length > 0 && (
            <Col span={24} style={{ marginBottom: 12 }}>
              <div style={{ background: '#FFF8F4', border: '1px solid #F5D8CC', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
                <span style={{ fontWeight: 600, color: '#D85A30' }}>冲抵预览：</span>
                {allocations.map(a => (
                  <Tag key={a.no} style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 }}>
                    {a.no.slice(-8)} 冲 ¥{fmtMoney(a.inc)} <span style={{ color: '#8c8c8c' }}>/ 应收 ¥{fmtMoney(a.rec)}</span>
                  </Tag>
                ))}
                {payAmount > totalRec + 0.001 && <span style={{ color: '#BA7517' }}>（超出应收 ¥{fmtMoney(payAmount - totalRec)} 记为客户预收）</span>}
              </div>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
}

// ===== 主页面 =====
export default function Receivables() {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [kw, setKw] = useState('');
  const [onlyUnpaid, setOnlyUnpaid] = useState(false);
  const [activeTab, setActiveTab] = useState('cust');
  const [payModal, setPayModal] = useState(false);
  const [initCust, setInitCust] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cs, ps] = await Promise.all([
        apiFetch('/api/crm/customers').then(r => r.json()),
        apiFetch('/api/crm/payments').then(r => r.json()),
      ]);
      setCustomers((cs.data || []).filter(c => c.name !== '待归属'));
      setPayments(ps.data || []);
    } catch (e) { message.error('加载失败'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // KPI（本月口径：本地时间）
  const now = new Date();
  const nowMonth = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
  const totalReceivable = round2(customers.reduce((s, c) => s + Math.max(0, Number(c.receivable) || 0), 0));
  const totalPaid = round2(payments.reduce((s, p) => s + (Number(p.amount) || 0), 0));
  const monthPaid = round2(payments.filter(p => String(p.date || '').startsWith(nowMonth)).reduce((s, p) => s + (Number(p.amount) || 0), 0));
  const unpaidCount = customers.filter(c => (Number(c.receivable) || 0) > 0.001).length;
  const totalAmount = round2(customers.reduce((s, c) => s + (Number(c.total_amount) || 0), 0));

  // 每客户最近回款时间 + 累计回款
  const payByCust = {};
  payments.forEach(p => {
    const k = p.customer_id;
    if (!payByCust[k]) payByCust[k] = { last: '', sum: 0 };
    if (String(p.date || '') > payByCust[k].last) payByCust[k].last = String(p.date || '');
    payByCust[k].sum = round2(payByCust[k].sum + (Number(p.amount) || 0));
  });

  // 客户表筛选
  const custRows = customers.filter(c => {
    if (onlyUnpaid && !((Number(c.receivable) || 0) > 0.001)) return false;
    if (kw && !String(c.name || '').toLowerCase().includes(kw.toLowerCase())) return false;
    return true;
  }).map(c => ({ ...c, last_pay_date: (payByCust[c.id] || {}).last || '', cust_pay_sum: (payByCust[c.id] || {}).sum || 0 }))
    .sort((a, b) => (Number(b.receivable) || 0) - (Number(a.receivable) || 0));

  // 流水筛选
  const payRows = payments.filter(p => {
    if (kw) {
      const hit = String(p.customer_name || '').toLowerCase().includes(kw.toLowerCase())
        || String(p.order_no || '').toLowerCase().includes(kw.toLowerCase())
        || String(p.remark || '').toLowerCase().includes(kw.toLowerCase());
      if (!hit) return false;
    }
    return true;
  });

  const delPayment = async (id) => {
    const res = await apiFetch(`/api/crm/payments/${id}`, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除并回滚订单已收'); load(); notifyDataChanged(); } else message.error(res.error || '删除失败');
  };

  const custColumns = [
    { title: '客户', dataIndex: 'name', width: 180, fixed: 'left', render: (v, r) => (
      <div>
        <div style={{ fontWeight: 600 }}>{v}</div>
        {r.level && r.level !== '普通' && <Tag color={LEVEL_COLOR[r.level] || 'default'} style={{ marginTop: 2 }}>{r.level}</Tag>}
      </div>
    ) },
    { title: '成交额', dataIndex: 'total_amount', align: 'right', width: 110, sorter: (a, b) => (a.total_amount || 0) - (b.total_amount || 0), render: v => <span style={{ color: '#26201C', fontWeight: 600 }}>¥{fmtMoney(v)}</span> },
    { title: '已收', dataIndex: 'total_paid', align: 'right', width: 110, sorter: (a, b) => (a.total_paid || 0) - (b.total_paid || 0), render: v => <span style={{ color: '#3F8600', fontWeight: 600 }}>¥{fmtMoney(v)}</span> },
    { title: '应收', dataIndex: 'receivable', align: 'right', width: 110, sorter: (a, b) => (a.receivable || 0) - (b.receivable || 0), render: v => v > 0.001
      ? <span style={{ color: '#D85A30', fontWeight: 700 }}>¥{fmtMoney(v)}</span>
      : <Tag color="green" style={{ marginRight: 0 }}>已结清</Tag> },
    { title: '回款率', key: 'rate', align: 'center', width: 90, sorter: (a, b) => ((a.total_amount || 0) ? (a.total_paid || 0) / (a.total_amount || 0) : 0) - ((b.total_amount || 0) ? (b.total_paid || 0) / (b.total_amount || 0) : 0),
      render: (_, r) => {
        const rate = (Number(r.total_amount) || 0) > 0 ? Math.round(((Number(r.total_paid) || 0) / Number(r.total_amount)) * 100) : 0;
        return <span style={{ fontWeight: 600, color: rate >= 100 ? '#3F8600' : rate >= 50 ? '#BA7517' : '#D85A30' }}>{rate}%</span>;
      } },
    { title: '累计回款', key: 'cust_pay_sum', align: 'right', width: 110, render: (_, r) => r.cust_pay_sum > 0 ? <span>¥{fmtMoney(r.cust_pay_sum)}</span> : <span style={{ color: '#bfbfbf' }}>-</span> },
    { title: '最近回款', key: 'last_pay_date', align: 'center', width: 105, sorter: (a, b) => String(a.last_pay_date).localeCompare(String(b.last_pay_date)),
      render: (_, r) => r.last_pay_date ? <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>{r.last_pay_date}</span> : <span style={{ color: '#bfbfbf' }}>-</span> },
    { title: '单数', dataIndex: 'order_count', align: 'center', width: 70, sorter: (a, b) => (a.order_count || 0) - (b.order_count || 0) },
    { title: '操作', align: 'right', width: 110, fixed: 'right', render: (_, r) => (
      <Button size="small" type="primary" ghost icon={<DollarOutlined />} onClick={() => { setInitCust(r.id); setPayModal(true); }}>登记回款</Button>
    ) },
  ];

  const payColumns = [
    { title: '回款日期', dataIndex: 'date', width: 105, render: v => <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>{v || '-'}</span> },
    { title: '客户', dataIndex: 'customer_name', width: 150, render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { title: '金额', dataIndex: 'amount', align: 'right', width: 110, render: v => <span style={{ color: '#3F8600', fontWeight: 700 }}>+¥{fmtMoney(v)}</span> },
    { title: '方式', dataIndex: 'method', width: 80, render: v => <Tag>{v || '-'}</Tag> },
    { title: '关联订单', dataIndex: 'order_no', ellipsis: true, render: v => v
      ? <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: '#2F54EB' }}>{v}</span>
      : <span style={{ color: '#bfbfbf', fontSize: 12 }}>不冲订单</span> },
    { title: '备注', dataIndex: 'remark', ellipsis: true, render: v => v || '-' },
    { title: '登记时间', dataIndex: 'created_at', width: 105, render: v => <span style={{ color: '#8c8c8c', fontSize: 12 }}>{v || '-'}</span> },
    { title: '操作', align: 'right', width: 70, render: (_, r) => (
      <Popconfirm title="删除此回款并回滚各订单已收？" onConfirm={() => delPayment(r.id)}>
        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
      </Popconfirm>
    ) },
  ];

  return (
    <div className="page-container">
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>客户回款</div>
          <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
            客户应收 / 已收 / 回款流水统一管理；登记回款可选关联订单按应收比例自动冲抵，删除回款自动回滚。
          </div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
          <Button type="primary" icon={<PayCircleOutlined />} onClick={() => { setInitCust(null); setPayModal(true); }}>登记回款</Button>
        </Space>
      </div>

      {/* ===== KPI ===== */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: '3px solid #D85A30', borderRadius: 6 }}>
            <Statistic title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>应收总额（{unpaidCount} 家待回款）</span>} value={totalReceivable} precision={2} prefix="¥"
              valueStyle={{ color: '#D85A30', fontWeight: 700, fontSize: 20 }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: '3px solid #3F8600', borderRadius: 6 }}>
            <Statistic title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>累计回款（{payments.length} 笔）</span>} value={totalPaid} precision={2} prefix="¥"
              valueStyle={{ color: '#3F8600', fontWeight: 700, fontSize: 20 }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: '3px solid #BA7517', borderRadius: 6 }}>
            <Statistic title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>本月回款（{nowMonth}）</span>} value={monthPaid} precision={2} prefix="¥"
              valueStyle={{ color: '#BA7517', fontWeight: 700, fontSize: 20 }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: '3px solid #1F5C99', borderRadius: 6 }}>
            <Statistic title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>整体回款率</span>}
              value={totalAmount > 0 ? Math.round((totalPaid / totalAmount) * 100) : 0} suffix="%"
              valueStyle={{ color: '#1F5C99', fontWeight: 700, fontSize: 20 }} />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>累计成交 ¥{fmtMoney(totalAmount)}</div>
          </Card>
        </Col>
      </Row>

      <Card size="small" styles={{ body: { padding: '12px 12px 4px 12px' } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: -16 }}
            items={[
              { key: 'cust', label: `客户应收（${custRows.length}）` },
              { key: 'flow', label: `回款流水（${payRows.length}）` },
            ]} />
          <Space>
            {activeTab === 'cust' && (
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>只看待回款 <Switch size="small" checked={onlyUnpaid} onChange={setOnlyUnpaid} /></span>
            )}
            <Input.Search allowClear placeholder="搜客户 / 订单号 / 备注" style={{ width: 220 }} value={kw} onChange={e => setKw(e.target.value)} />
          </Space>
        </div>

        {activeTab === 'cust' ? (
          <Table
            size="small" rowKey="id" loading={loading}
            columns={custColumns}
            dataSource={custRows}
            scroll={{ x: 1000 }}
            pagination={{ pageSize: 15, showSizeChanger: false, showTotal: t => `共 ${t} 家` }}
            locale={{ emptyText: '暂无客户应收数据' }}
          />
        ) : (
          <Table
            size="small" rowKey="id" loading={loading}
            columns={payColumns}
            dataSource={payRows}
            scroll={{ x: 900 }}
            pagination={{ pageSize: 15, showSizeChanger: false, showTotal: t => `共 ${t} 笔` }}
            locale={{ emptyText: '暂无回款记录，点右上角「登记回款」录入第一笔' }}
          />
        )}
      </Card>

      {payModal && (
        <PaymentModal
          open={payModal}
          customers={customers}
          initCustomerId={initCust}
          onCancel={() => setPayModal(false)}
          onDone={() => { setPayModal(false); load(); notifyDataChanged(); }}
        />
      )}
    </div>
  );
}
